import { getThemeClass, getThemeComponentGroup } from "@/shared/lib/themeComponentClasses";
import { toTitleCaseUi } from "@/shared/utils/titleCase";
import clsx from "clsx";
import { Controller, type FieldPath, type FieldValues } from "react-hook-form";

import type { TextareaInputProps } from "@/shared/types";
import { formatCharacterCount, isCharacterLimitExceeded } from "@/shared/utils/characterCount";
import { inputClassName } from "./TextInput";
import { WrapperInput } from "./WrapperInput";

export function TextareaInput<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  placeholder,
  description,
  disabled,
  required,
  className,
  rows = 5,
  maxLength,
}: TextareaInputProps<TFieldValues, TName>) {
  const inputClasses = getThemeComponentGroup("input");
  const placeholderText = toTitleCaseUi(placeholder || "");
  const textareaMinHeight = `${Math.max(rows, 1) * 24 + 20}px`;

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <WrapperInput
          label={label}
          error={fieldState.error?.message}
          description={description}
          required={required}
          className={className}
        >
          <textarea
            {...field}
            id={field.name}
            data-testid={field.name}
            value={field.value ?? ""}
            rows={rows}
            placeholder={placeholderText}
            disabled={disabled}
            style={{ minHeight: textareaMinHeight }}
            className={clsx(
              inputClassName,
              "h-auto py-3 thin-scrollbar",
              getThemeClass(inputClasses.borderColor, "border-theme-border-strong"),
              getThemeClass(inputClasses.bgColor, "bg-theme-surface"),
              getThemeClass(inputClasses.textColor, "text-theme-text"),
              getThemeClass(
                inputClasses.placeholderColor,
                "placeholder:text-theme-text-placeholder"
              ),
              getThemeClass(inputClasses.focusBorderColor, "focus:border-theme-primary"),
              "focus:ring-2",
              getThemeClass(inputClasses.focusRingColor, "focus:ring-theme-primary/10"),
              inputClasses.base,
              fieldState.error &&
                clsx(
                  inputClasses.errorBorderColor || "border-theme-danger-bright",
                  "focus:ring-theme-danger-bright/10"
                )
            )}
          />
          {maxLength ? (
            <div className="flex justify-end">
              <p
                className={clsx(
                  "text-xs",
                  isCharacterLimitExceeded(field.value, maxLength)
                    ? "text-theme-danger-bright"
                    : "text-theme-text-soft"
                )}
              >
                {formatCharacterCount(field.value, maxLength)}
              </p>
            </div>
          ) : null}
        </WrapperInput>
      )}
    />
  );
}
